import api from "../../api/client";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Avatar,
  Descriptions,
  Tag,
  Tabs,
  Form,
  Button,
  message,
  Spin,
  Table,
  Collapse,
  Statistic,
  DatePicker,
  Space,
  Typography,
  Divider,
  Timeline,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Line } from "@ant-design/plots";
import StakeholderForm from "./StakeholderForm";
import { formatKES } from "../../utils/currency";
import { Link } from "react-router-dom";
import { useFarmProfile } from "../../contexts/FarmProfileContext";
import { KarlaRegularBase64 } from "../../fonts/karla-regular";
import { KarlaBoldBase64 } from "../../fonts/karla-bold";

const { TabPane } = Tabs;
const { Panel } = Collapse;
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const StakeholderDetailPage = () => {
  const { id } = useParams();
  const [stakeholder, setStakeholder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [statementData, setStatementData] = useState(null);
  const [statementDateRange, setStatementDateRange] = useState([
    dayjs().startOf("month"),
    dayjs(),
  ]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [commentContent, setCommentContent] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  const { profile } = useFarmProfile();

  // Fetch all data
  const fetchStakeholder = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/stakeholders/${id}`);
      setStakeholder(data);
    } catch (err) {
      message.error("Failed to load stakeholder");
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const { data } = await api.get(`/api/stakeholders/${id}/comments`);
      setComments(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchInvoices = async () => {
    try {
      const { data } = await api.get(`/api/stakeholders/${id}/invoices`);
      setInvoices(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPayments = async () => {
    try {
      const { data } = await api.get(`/api/stakeholders/${id}/payments`);
      setPayments(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStatement = async (from, to) => {
    try {
      const { data } = await api.get(`/api/stakeholders/${id}/statement`, {
        params: { from_date: from, to_date: to },
      });
      setStatementData(data);
    } catch (err) {
      message.error("Failed to load statement");
    }
  };

  useEffect(() => {
    if (id) {
      fetchStakeholder();
      fetchComments();
      fetchInvoices();
      fetchPayments();
    }
  }, [id]);

  useEffect(() => {
    if (statementDateRange && statementDateRange.length === 2) {
      fetchStatement(
        statementDateRange[0].format("YYYY-MM-DD"),
        statementDateRange[1].format("YYYY-MM-DD"),
      );
    }
  }, [statementDateRange]);

  const addComment = async () => {
    if (!commentContent.trim()) {
      message.warning("Please enter a comment");
      return;
    }
    setSubmittingComment(true);
    try {
      await api.post(`/api/stakeholders/${id}/comments`, {
        comment: commentContent,
        user_id: 1,
      });
      message.success("Comment added");
      setCommentContent("");
      fetchComments();
    } catch (err) {
      message.error("Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  function registerKarlaFont(doc) {
    doc.addFileToVFS("Karla-Regular.ttf", KarlaRegularBase64);
    doc.addFont("Karla-Regular.ttf", "Karla", "normal");
    doc.addFileToVFS("Karla-Bold.ttf", KarlaBoldBase64);
    doc.addFont("Karla-Bold.ttf", "Karla", "bold");
  }

  // Works whether the installed jspdf-autotable exposes lastAutoTable (v3+)
  // or previousAutoTable (v2), and never throws if neither is set yet.
  function getFinalY(doc, fallback) {
    return (
      doc.lastAutoTable?.finalY ?? doc.previousAutoTable?.finalY ?? fallback
    );
  }

  const money = (n) =>
    (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

  const exportStatementPDF = () => {
    if (!statementData || !profile) return;

    try {
      const doc = new jsPDF();
      registerKarlaFont(doc);
      doc.setFont("Karla", "normal");

      const pageWidth = doc.internal.pageSize.getWidth();
      const rightColX = 115;

      // --- Logo (top-left) ---
      if (profile.logo_base64) {
        doc.addImage(profile.logo_base64, "PNG", 10, 4, 60, 50);
      }

      // --- Company block (top-right) ---
      doc.setFontSize(12);
      doc.setFont("Karla", "bold");
      doc.text(profile.name || "", pageWidth - 14, 18, { align: "right" });
      doc.setFont("Karla", "normal");
      doc.setFontSize(12);
      let y = 30;
      const companyLines = [
        profile.company_id ? `Company ID : ${profile.company_id}` : null,
        profile.address2 || null,
        profile.address1 || null,
        // profile.town || null,
        profile.phone1
          ? `Tel: ${profile.phone1}${
              profile.phone2 ? " | " + profile.phone2 : ""
            }`
          : null,
        profile.email || null,
      ].filter(Boolean);
      companyLines.forEach((line) => {
        doc.text(line, pageWidth - 14, y, { align: "right" });
        y += 5;
      });

      // --- "To" block ---
      doc.setFontSize(12);
      doc.text("To", 14, 50);
      doc.setFont("Karla", "bold");
      doc.text(
        stakeholder?.display_name
          ? stakeholder.display_name
          : stakeholder?.name || "N/A",
        14,
        56,
      );
      doc.setFont("Karla", "normal");

      // --- Statement title ---
      doc.setFontSize(13);
      doc.setFont("Karla", "bold");
      doc.text("Statement of Accounts", pageWidth - 14, y + 6, {
        align: "right",
      });
      doc.setDrawColor(0);
      doc.line(rightColX, y + 8, pageWidth - 14, y + 8);
      doc.setFontSize(10);
      doc.setFont("Karla", "normal");
      doc.text(
        `${statementDateRange[0].format(
          "DD MMM YYYY",
        )} To ${statementDateRange[1].format("DD MMM YYYY")}`,
        pageWidth - 14,
        y + 13,
        { align: "right" },
      );
      doc.line(rightColX, y + 16, pageWidth - 14, y + 16);

      const invoicedAmount = statementData.transactions
        .filter((t) => t.type === "Invoice")
        .reduce((sum, t) => sum + t.amount, 0);
      const amountReceived = statementData.transactions
        .filter((t) => t.type === "Payment")
        .reduce((sum, t) => sum + t.amount, 0);
      const closingBalance = statementData.transactions.length
        ? statementData.transactions[statementData.transactions.length - 1]
            .runningBalance
        : statementData.openingBalance;

      // --- Account Summary box ---
      doc.autoTable({
        startY: y + 20,
        margin: { left: rightColX },
        tableWidth: pageWidth - 14 - rightColX,
        theme: "plain",
        styles: { font: "Karla", fontSize: 10, cellPadding: 2 },
        head: [
          [
            {
              content: "Account Summary",
              colSpan: 2,
              styles: {
                fillColor: [60, 60, 60],
                textColor: 255,
                fontStyle: "bold",
                fontSize: 10,
                font: "Karla",
              },
            },
          ],
        ],
        body: [
          ["Opening Balance", `KES ${money(statementData.openingBalance)}`],
          ["Invoiced Amount", `KES ${money(invoicedAmount)}`],
          [
            stakeholder.type === "Customer" ? "Amount Received" : "Amount Paid",
            `KES ${money(amountReceived)}`,
          ],
          ["Balance Due", `KES ${money(closingBalance)}`],
        ],
        alternateRowStyles: { fillColor: [247, 247, 247] },
        columnStyles: { 1: { halign: "right" } },
        didParseCell: (data) => {
          if (data.row.section === "body" && data.row.index === 3) {
            data.cell.styles.fontStyle = "bold";
          }
        },
      });

      const tableStartY = getFinalY(doc, y + 40) + 10;

      // --- Transactions table (opening balance as first row) ---
      const rows = [
        {
          date: statementDateRange[0].toISOString(),
          type: "***Opening Balance***",
          reference: "",
          amount: statementData.openingBalance,
          isOpening: true,
          runningBalance: statementData.openingBalance,
        },
        ...statementData.transactions,
      ];

      doc.autoTable({
        startY: tableStartY,
        styles: {
          font: "Karla",
          fontSize: 10,
          cellPadding: 3,
          valign: "middle",
        },
        headStyles: {
          font: "Karla",
          fontStyle: "bold",
          fillColor: [38, 38, 38],
          textColor: 255,
          fontSize: 10,
        },
        alternateRowStyles: { fillColor: [247, 247, 247] },
        head: [
          [
            { content: "Date" },
            { content: "Transactions" },
            { content: "Details" },
            { content: "Vatable Amount", styles: { halign: "right" } },
            { content: "Payments", styles: { halign: "right" } },
            { content: "Balance", styles: { halign: "right" } },
          ],
        ],
        body: rows.map((t) => [
          dayjs(t.date).format("DD MMM YYYY"),
          t.type,
          t.isOpening ? "" : buildDetailsText(t),
          t.isOpening || t.type === "Invoice" ? money(t.amount) : "",
          t.type === "Payment" ? money(t.amount) : "",
          money(t.runningBalance),
        ]),
        columnStyles: {
          3: { halign: "right" },
          4: { halign: "right" },
          5: { halign: "right" },
        },
        showFoot: "lastPage", // ← add this
        foot: [
          [
            "",
            "",
            "Opening Balance",
            "",
            "",
            `KES ${money(statementData.openingBalance)}`,
          ],
          ["", "", "Invoiced Amount", "", "", `KES ${money(invoicedAmount)}`],
          [
            "",
            "",
            stakeholder.type === "Customer" ? "Amount Received" : "Amount Paid",
            "",
            "",
            `KES ${money(amountReceived)}`,
          ],
          ["", "", "Balance Due", "", "", `KES ${money(closingBalance)}`],
        ],
        footStyles: {
          font: "Karla",
          fontStyle: "bold",
          fillColor: 255,
          fontSize: 10,
          lineWidth: { top: 0.3 },
          halign: "right",
        },
        didParseCell: (data) => {
          if (data.section === "foot" && data.row.index === 3) {
            data.cell.styles.fontSize = 10;
          }
        },
        didDrawPage: () => {
          doc.setFont("Karla", "normal");
          doc.setFontSize(10);
          doc.text(
            `Page ${
              doc.internal.getCurrentPageInfo().pageNumber
            } of ${doc.internal.getNumberOfPages()}`,
            pageWidth - 30,
            doc.internal.pageSize.getHeight() - 10,
          );
        },
      });

      const finalY = getFinalY(doc, tableStartY);
      doc.setFont("Karla", "bold");
      doc.setFontSize(10);
      [
        ["Opening Balance", statementData.openingBalance],
        ["Invoiced Amount", invoicedAmount],
        [
          stakeholder.type === "Customer" ? "Amount Received" : "Amount Paid",
          amountReceived,
        ],
        ["Balance Due", closingBalance],
      ].forEach(([label, val], i) => {
        const rowY = finalY + 8 + i * 6;
        doc.text(label, pageWidth - 60, rowY, { align: "right" });
        doc.text(`KES ${money(val)}`, pageWidth - 14, rowY, { align: "right" });
      });

      doc.save(
        `statement_${stakeholder.name.replace(/\s+/g, "_")}_${dayjs().format(
          "YYYYMMDD",
        )}.pdf`,
      );
    } catch (err) {
      console.error("Statement PDF generation failed:", err);
      // surface this to the user instead of failing silently — e.g. message.error(...)
    }
  };

  function buildDetailsText(t) {
    if (t.type === "Invoice") {
      return t.reference
        ? `${t.reference} - due on ${dayjs(t.recorded).format("DD MMM YYYY")}`
        : t.reference;
    }
    console.log("see allocations", t);
    // if (t.type === "Payment" && t.allocations?.length) {
    //     return [t.reference, ...t.allocations.map((a) => `KES${money(a.amount)} for payment of ${a.invoiceRef}`)].join("\n");
    //   }

    if (t.type === "Payment") {
      return t.reference
        ? `KES${money(t.amount)} for payment of ${t?.reference}`
        : t.reference;
    }
  }

  if (loading) return <Spin tip="Loading..." />;
  if (!stakeholder) return <div>Stakeholder not found</div>;

  // Chart data (last 6 months income)
  const last6Months = Array.from({ length: 6 }, (_, i) =>
    dayjs()
      .subtract(5 - i, "month")
      .format("MMM YYYY"),
  );

  const incomeData = last6Months.map((month) => ({
    month,
    income: Math.floor(Math.random() * 5000),
  }));

  const handleBack = () => {
    window.history.back();
  };

  return (
    <Card
      title={stakeholder?.name}
      extra={
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      }
    >
      <Tabs defaultActiveKey="overview">
        <TabPane tab="Overview" key="overview">
          <Row gutter={24}>
            {/* LEFT COLUMN: Accordion sections */}
            <Col span={8}>
              <Collapse defaultActiveKey={["profile"]} bordered={false}>
                {/* Profile Section */}
                <Panel header="Profile" key="profile">
                  <Row gutter={16}>
                    <Col span={8} style={{ textAlign: "center" }}>
                      <Avatar
                        size={60}
                        src={`https://ui-avatars.com/api/?name=${stakeholder.name}&background=1677ff&color=fff`}
                      />
                    </Col>
                    <Col span={16}>
                      <Title level={5}>
                        {stakeholder.salutation} {stakeholder.name}
                      </Title>

                      {stakeholder?.display_name && (
                        <>
                          {" "}
                          <Text>{stakeholder?.display_name}</Text>
                          <br />
                        </>
                      )}

                      <Text type="secondary">
                        <MailOutlined /> {stakeholder.email}
                      </Text>
                      <br />
                      <Text type="secondary">
                        <PhoneOutlined /> {stakeholder.phone}
                      </Text>
                      <br />
                      <Text type="secondary" className="text-capitalize">
                        <ExclamationCircleOutlined /> {stakeholder.type}
                      </Text>
                      <br />

                      <Button
                        size="small"
                        type="ghost"
                        icon={<EditOutlined />}
                        onClick={() => setEditModalVisible(true)}
                        style={{ marginTop: 16 }}
                      >
                        Edit
                      </Button>
                    </Col>
                  </Row>
                </Panel>

                {/* Other Details Section */}
                <Panel header="Other Details" key="other">
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Company Name">
                      {stakeholder.company_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Currency">
                      {stakeholder.currency || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Type" className="text-capitalize">
                      {stakeholder.type || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="KRA PIN">
                      {stakeholder.kra_pin || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Payment Terms">
                      {stakeholder.payment_terms || "Due on Receipt"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Address">
                      {stakeholder.location || stakeholder.address}
                    </Descriptions.Item>
                    <Descriptions.Item label="Language">
                      English
                    </Descriptions.Item>
                  </Descriptions>
                </Panel>

                {/* Contact Details Section */}
                <Panel header="Contact Details" key="contact">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Card title="Billing Address" size="small">
                        {stakeholder.address || "No Billing Address"}
                        <br />
                        <Button type="link" size="small">
                          New Address
                        </Button>
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card title="Shipping Address" size="small">
                        {stakeholder.address || "No Shipping Address"}
                        <br />
                        <Button type="link" size="small">
                          New Address
                        </Button>
                      </Card>
                    </Col>
                  </Row>
                </Panel>
              </Collapse>
            </Col>

            {/* RIGHT COLUMN: Income Chart + Receivables */}
            <Col span={16}>
              {stakeholder.type === "customer" ? (
                <Card style={{ marginBottom: 16 }} size="small">
                  <Space>
                    <Statistic
                      title="Outstanding Receivables"
                      value={stakeholder.receivable || 0}
                      prefix="KES "
                    />
                    <Statistic
                      title="Unused Credits"
                      value={stakeholder.credits || 0}
                      prefix="KES "
                    />
                  </Space>
                </Card>
              ) : stakeholder.type === "supplier" ? (
                <Card style={{ marginBottom: 16 }} size="small">
                  <Space>
                    <Statistic
                      title="Outstanding Payables"
                      value={stakeholder.payable || 0}
                      prefix="KES "
                    />
                    <Statistic title="Unused Credits" value={0} prefix="KES " />
                  </Space>
                </Card>
              ) : null}
              {/* <Card title="Income Trend (Last 6 Months)" size="small">
                <Line data={incomeData} xField="month" yField="income" smooth />
                <div style={{ textAlign: "right", marginTop: 8 }}>
                  Total Income: KES{" "}
                  {formatKES(incomeData.reduce((s, i) => s + i.income, 0))}
                </div>
              </Card> */}

              <Card title="Income Trend (Last 6 Months)" size="small">
                <Line data={incomeData} xField="month" yField="income" smooth />
                <div style={{ textAlign: "right", marginTop: 8 }}>
                  Total Income:
                  {formatKES(incomeData.reduce((s, i) => s + i.income, 0))}
                </div>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Comments" key="comments">
          <div style={{ marginBottom: 16 }}>
            <ReactQuill
              theme="snow"
              value={commentContent}
              onChange={setCommentContent}
              placeholder="Write a comment... (rich text)"
              style={{ height: 200, marginBottom: 48 }}
            />
            <Button
              type="primary"
              onClick={addComment}
              loading={submittingComment}
              style={{ marginTop: 16 }}
            >
              Post Comment
            </Button>
          </div>
          <Divider />
          <Timeline>
            {comments.map((c) => (
              <Timeline.Item key={c.id}>
                <strong>
                  {c.firstname} {c.lastname}
                </strong>
                <div dangerouslySetInnerHTML={{ __html: c.comment }} />
                <Text type="secondary">{dayjs(c.created_at).fromNow()}</Text>
              </Timeline.Item>
            ))}
          </Timeline>
        </TabPane>

        <TabPane tab="Transactions" key="transactions">
          <Collapse defaultActiveKey={["invoices"]}>
            <Panel header="Invoices" key="invoices">
              <Table
                size="small"
                dataSource={invoices}
                columns={[
                  {
                    title: "Invoice #",
                    dataIndex: "invoice_number",
                    render: (_, v) =>
                      stakeholder.type === "customer" ? (
                        <Link to={`/sales/view/${v.id}`}>
                          {v.invoice_number}
                        </Link>
                      ) : (
                        v.invoice_number
                      ),
                  },
                  {
                    title: "Date",
                    dataIndex: "transaction_date",
                    render: (d) => dayjs(d).format("DD MMM YYYY"),
                  },
                  {
                    title: "Amount",
                    dataIndex: "total_amount",
                    render: (v) => formatKES(v),
                  },
                  {
                    title: "Paid",
                    dataIndex: "paid_amount",
                    render: (v) => formatKES(v),
                  },
                  {
                    title: "Status",
                    dataIndex: "status",
                    render: (s) => (
                      <Tag color={s === "fully_paid" ? "green" : "orange"}>
                        {s}
                      </Tag>
                    ),
                  },
                ]}
                rowKey="id"
              />
            </Panel>
            <Panel header="Payments" key="payments">
              <Table
                size="small"
                dataSource={payments}
                columns={[
                  {
                    title: "Date",
                    dataIndex: "paymentDate",
                    render: (d) => dayjs(d).format("DD MMM sYYYY"),
                  },
                  { title: "Reference", dataIndex: "reference" },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    render: (v) => formatKES(v),
                  },
                  {
                    title: "Method",
                    dataIndex: "paymentMethod",
                    render: (v) => <span className="text-capitalize">{v}</span>,
                  },
                ]}
                rowKey="id"
              />
            </Panel>
          </Collapse>
        </TabPane>

        <TabPane tab="Statement" key="statement">
          <Space style={{ marginBottom: 16 }}>
            <RangePicker
              value={statementDateRange}
              onChange={setStatementDateRange}
            />
            <Button type="primary" onClick={exportStatementPDF}>
              Export PDF
            </Button>
          </Space>
          {statementData && (
            <Card>
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <Title level={4}>
                  Statement for {stakeholder.name}
                </Title>
                <Text>
                  {statementDateRange[0].format("DD MMM YYYY")} -{" "}
                  {statementDateRange[1].format("DD MMM YYYY")}
                </Text>
              </div>

              {/* Compute summary from transactions */}
              {(() => {
                const invoicedAmount = statementData.transactions
                  .filter((t) => t.type === "Invoice")
                  .reduce((sum, t) => sum + t.amount, 0);
                const amountReceived = statementData.transactions
                  .filter((t) => t.type === "Payment")
                  .reduce((sum, t) => sum + t.amount, 0);
                const balanceDue =
                  statementData.openingBalance +
                  invoicedAmount -
                  amountReceived;

                return (
                  <Row gutter={16}>
                    <Col span={6}>
                      <Statistic
                        title="Opening Balance"
                        value={formatKES(statementData.openingBalance)}
                        // prefix="KES "
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Invoiced Amount"
                        value={formatKES(invoicedAmount)}
                        // prefix="KES "
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Amount Received"
                        value={formatKES(amountReceived)}
                        // prefix="KES "
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Balance Due"
                        value={formatKES(balanceDue)}
                        // prefix="KES "
                      />
                    </Col>
                  </Row>
                );
              })()}

              <Table
                size="small"
                dataSource={statementData.transactions}
                columns={[
                  {
                    title: "Date",
                    dataIndex: "date",
                    render: (d) => dayjs(d).format("DD MMM YYYY"),
                  },
                  { title: "Transactions", dataIndex: "type" },
                  { title: "Details", dataIndex: "reference" },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    render: (v, r) =>
                      r.type === "Invoice" ? formatKES(v) : "",
                  },
                  {
                    title: "Payments",
                    dataIndex: "amount",
                    render: (v, r) =>
                      r.type === "Payment" ? formatKES(v) : "",
                  },
                  {
                    title: "Balance",
                    dataIndex: "runningBalance",
                    render: (v) => formatKES(v),
                  },
                ]}
                rowKey={(r) => `${r.date}-${r.reference}`}
                pagination={false}
                footer={() => (
                  <strong>
                    Balance Due:{" "}
                    {formatKES(
                      statementData.transactions.length
                        ? statementData.transactions[
                            statementData.transactions.length - 1
                          ].runningBalance
                        : statementData.openingBalance,
                    )}
                  </strong>
                )}
              />
            </Card>
          )}
        </TabPane>
      </Tabs>

      <StakeholderForm
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onSuccess={() => {
          setEditModalVisible(false);
          fetchStakeholder();
        }}
        initialData={stakeholder}
      />
    </Card>
  );
};

export default StakeholderDetailPage;
